import { Button } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { InboxItem } from "~/common/agent-run/agents";
import { fileUrl } from "~/common/media/api";
import { PlatformPreview } from "~/common/publishing/platform-preview";
import { smartDistribution } from "~/core/api/smart-distribution";
import { pick, useI18n, useT } from "~/core/i18n";
import "../features/platform-publishing/smart-distribution.css";

/** A workflow approval shows the exact content it authorizes, never raw JSON. */
export function DistributionWait({ item, onReady }: { item: InboxItem; onReady: (ready: boolean) => void }) {
	const t = useT();
	const { locale } = useI18n();
	const [active, setActive] = useState("");
	const ref = item.distribution!;
	const query = useQuery({
		queryKey: [item.entity_id, "distribution-wait", ref.brief_id],
		queryFn: () => smartDistribution.read(item.entity_id, ref.brief_id!),
		refetchOnWindowFocus: true,
	});
	const brief = query.data;
	const consistent =
		!!brief && brief.revision === ref.revision && JSON.stringify(brief.drafts) === JSON.stringify(ref.drafts);
	const ready =
		consistent && !!brief.drafts?.length && !brief.drafts.some((d) => d.missing_information.length || d.issues?.length);
	useEffect(() => onReady(ready), [ready, onReady]);
	if (query.isPending) return <p>{t("smartDistribution.working")}</p>;
	if (!consistent) return <p role="alert">{t("errors.publishing_source_changed")}</p>;
	const drafts = brief.drafts ?? [];
	const current = drafts.find((d) => d.platform_id === active) ?? drafts[0];
	const choice = brief.selected?.find((c) => c.platform_id === current?.platform_id);
	const model = choice?.platform.content_types.find((f) => f.id === current?.format);
	const assets = brief.input.materials.flatMap((m) => m.assets ?? []);
	const previewUrl = (value: string) => assets.reduce((out, a) => out.split(a.url).join(fileUrl(a.file_id)), value);
	return (
		<div className="stack">
			<nav className="smart-draft-nav" aria-label={t("smartDistribution.reviewPlatforms")}>
				{drafts.map((d) => (
					<Button
						key={d.platform_id}
						variant={current?.platform_id === d.platform_id ? "outline" : "ghost"}
						aria-pressed={current?.platform_id === d.platform_id}
						onClick={() => setActive(d.platform_id)}
					>
						{brief.selected?.find((c) => c.platform_id === d.platform_id)?.platform.name ?? d.platform_id}
					</Button>
				))}
			</nav>
			{current && model && (
				<PlatformPreview
					platform={{
						id: current.platform_id,
						name: choice!.platform.name,
						preview: {
							...model.preview,
							fields: model.preview.fields.map((f) => ({ ...f, label: f.label ?? [f.key, f.key] })),
							media: model.preview.media ?? null,
						},
					}}
					payload={{
						fields: Object.fromEntries(Object.entries(current.fields).map(([k, v]) => [k, previewUrl(v)])),
						media: current.media.map((m) => ({ url: previewUrl(m.url), alt: "" })),
					}}
				/>
			)}
			{current && (
				<details>
					<summary>{t("smartDistribution.viewFields")}</summary>
					{Object.entries(current.fields).map(([key, value]) => {
						const field = model?.preview.fields.find((f) => f.key === key);
						return (
							<div key={key}>
								<strong>{field?.label ? pick(locale, field.label) : key}</strong>
								<p style={{ whiteSpace: "pre-wrap" }}>{value}</p>
							</div>
						);
					})}
				</details>
			)}
			{drafts.some((d) => d.missing_information.length || d.issues?.length) && <p>{t("smartDistribution.missing")}</p>}
		</div>
	);
}
